import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import SearchableSelect from '../components/SearchableSelect';
import { listOptions, useConfig } from '../context/ConfigContext';

interface EmployeeOption {
  id: number;
  full_name: string;
  [key: string]: unknown;
}

// TS port of the source (Falah) app's pages/ProcurementReceiptForm.jsx.
// Creates a Goods Receipt (NOT a Revenue Receipt — see ReceiptForm.tsx for
// that unrelated page) via
// `/purchase-orders/{id}/goods-receipts` (ProcurementFulfillmentController::
// storeGoodsReceipt()). `condition` is list-item-backed
// (`goods_receipt_condition`, in ListItem::KEYS) even though the backend's
// own validation rule for it is just `required|string|max:255` (no
// Rule::exists) — the dropdown here is a UX convenience, not an enforced
// enum.
//
// create_asset (capex/opex fix): checking it capitalizes this receiving as
// a Fixed Asset instead of letting its payment post to Procurement Expense
// in the GL feed — see GlBatchGenerator::procurementExpenseLines()/
// assetCapitalizationLines(). Description/quantity/cost/vendor for the new
// Asset are derived server-side from the purchase request/order, never
// re-keyed here.
interface ReceiptFormState {
  invoice_number: string;
  received_at: string;
  quantity_received: string;
  condition: string;
  remarks: string;
  status: string;
  create_asset: boolean;
  asset_category: string;
  asset_department: string;
  asset_location: string;
  asset_custodian_employee_id: string | number;
  asset_useful_life_years: string;
  asset_residual_value: string;
}

const emptyForm: ReceiptFormState = {
  invoice_number: '',
  received_at: new Date().toISOString().slice(0, 10),
  quantity_received: '',
  condition: '',
  remarks: '',
  status: 'fully_received',
  create_asset: false,
  asset_category: '',
  asset_department: '',
  asset_location: '',
  asset_custodian_employee_id: '',
  asset_useful_life_years: '',
  asset_residual_value: '',
};

interface OrderOption {
  id: number;
  po_number: string;
  vendor_name: string;
  status: string;
}

type FormErrors = Record<string, string[]>;

export function ProcurementReceiptForm() {
  const navigate = useNavigate();
  const { lists } = useConfig();
  const [orders, setOrders] = useState<OrderOption[] | null>(null);
  const [purchaseOrderId, setPurchaseOrderId] = useState('');
  const [form, setForm] = useState<ReceiptFormState>(emptyForm);
  const [errors, setErrors] = useState<FormErrors>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get<{ data: OrderOption[] }>('/purchase-orders', { params: { per_page: 100 } })
      .then((res) => setOrders(res.data.data.filter((po) => !['completed', 'cancelled'].includes(po.status))));
  }, []);

  const set =
    (key: keyof ReceiptFormState) =>
    (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm({ ...form, [key]: e.target.value });

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (saving) return;
    if (!purchaseOrderId) {
      setErrors({ general: ['Select an order to record a receipt against.'] });
      return;
    }
    setSaving(true);
    try {
      await api.post(`/purchase-orders/${purchaseOrderId}/goods-receipts`, form);
      navigate('/procurement/receiving');
    } catch (err) {
      setErrors(
        err instanceof ApiError && err.errors ? err.errors : { general: [err instanceof ApiError ? err.message : 'Save failed.'] }
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <header className="page-header">
        <h1>Add Receiving</h1>
      </header>

      <div className="card">
        <form onSubmit={save} className="form-grid">
          {errors.general && <div className="alert alert-error">{errors.general[0]}</div>}
          <label className="span-2">
            Order *
            <select value={purchaseOrderId} onChange={(e) => setPurchaseOrderId(e.target.value)} required>
              <option value="">{orders === null ? 'Loading…' : 'Select an order…'}</option>
              {orders?.map((po) => (
                <option key={po.id} value={po.id}>
                  {po.po_number} — {po.vendor_name}
                </option>
              ))}
            </select>
            {orders?.length === 0 && <small className="muted">No open orders to receive against.</small>}
          </label>
          <label>
            Invoice number
            <input value={form.invoice_number} onChange={set('invoice_number')} />
            {errors.invoice_number && <small className="error">{errors.invoice_number[0]}</small>}
          </label>
          <label>
            Received date *
            <input type="date" value={form.received_at} onChange={set('received_at')} required />
            {errors.received_at && <small className="error">{errors.received_at[0]}</small>}
          </label>
          <label>
            Status *
            <select value={form.status} onChange={set('status')} required>
              <option value="fully_received">Fully Received</option>
              <option value="partially_received">Partially Received</option>
              <option value="issue_reported">Issue Reported</option>
            </select>
          </label>
          <label>
            Quantity received
            <input value={form.quantity_received} onChange={set('quantity_received')} />
          </label>
          <label>
            Condition *
            <select value={form.condition} onChange={set('condition')} required>
              <option value="">Select…</option>
              {listOptions(lists, 'goods_receipt_condition').map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
            {errors.condition && <small className="error">{errors.condition[0]}</small>}
          </label>
          <label className="span-2">
            Remarks
            <textarea rows={2} value={form.remarks} onChange={set('remarks')} />
          </label>
          <label className="span-2 checkbox-label">
            <input
              type="checkbox"
              checked={form.create_asset}
              onChange={(e) => setForm({ ...form, create_asset: e.target.checked })}
            />
            Create Asset (capital item)
          </label>
          <small className="muted span-2">
            Excludes this payment from Procurement Expense in the GL feed and creates a Fixed Asset record instead.
          </small>
          {form.create_asset && (
            <>
              <label>
                Asset Category *
                <select value={form.asset_category} onChange={set('asset_category')} required>
                  <option value="">Select…</option>
                  {listOptions(lists, 'asset_category').map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
                {errors.asset_category && <small className="error">{errors.asset_category[0]}</small>}
              </label>
              <label>
                Asset Department *
                <select value={form.asset_department} onChange={set('asset_department')} required>
                  <option value="">Select…</option>
                  {listOptions(lists, 'asset_department').map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
                {errors.asset_department && <small className="error">{errors.asset_department[0]}</small>}
              </label>
              <label>
                Asset Location
                <input value={form.asset_location} onChange={set('asset_location')} />
              </label>
              <label>
                Custodian
                <SearchableSelect<EmployeeOption>
                  endpoint="/employees"
                  value={form.asset_custodian_employee_id}
                  onChange={(id) => setForm({ ...form, asset_custodian_employee_id: id })}
                  getLabel={(e) => e.full_name}
                  placeholder="Search employees…"
                />
              </label>
              <label>
                Useful Life (Years) *
                <input
                  type="number"
                  min="1"
                  value={form.asset_useful_life_years}
                  onChange={set('asset_useful_life_years')}
                  required
                />
                {errors.asset_useful_life_years && <small className="error">{errors.asset_useful_life_years[0]}</small>}
              </label>
              <label>
                Residual Value
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.asset_residual_value}
                  onChange={set('asset_residual_value')}
                />
              </label>
            </>
          )}
          <div className="form-actions span-2">
            <Link className="btn btn-ghost" to="/procurement/receiving">
              Cancel
            </Link>
            <button className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
